import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CountryRegionToken, CountryToken } from '@sleed/bizcommerce-api';
import { countryRegionsApi, countriesApi } from '~/lib/api/client';
import { formToToken, type CountryRegionFormValues } from '~/lib/schemas/country-region';

export const countryRegionKeys = {
	all: ['country-regions'] as const,
	list: () => [...countryRegionKeys.all, 'list'] as const,
	countries: () => [...countryRegionKeys.all, 'countries'] as const
};

/** Full region list (legacy `/countryregions/getall`, loadMode 'raw'). */
export function useCountryRegionsList() {
	return useQuery<CountryRegionToken[]>({
		queryKey: countryRegionKeys.list(),
		queryFn: async () => (await countryRegionsApi.getAll()).data.items ?? []
	});
}

/** Country lookup ({id, nm}) for the parent column + edit dropdown. */
export function useRegionCountries() {
	return useQuery<CountryToken[]>({
		queryKey: countryRegionKeys.countries(),
		queryFn: async () => (await countriesApi.getAll()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useCountryRegionMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: countryRegionKeys.list() });

	const create = useMutation({
		mutationFn: async (form: CountryRegionFormValues) => (await countryRegionsApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: CountryRegionFormValues) => (await countryRegionsApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await countryRegionsApi.remove(id)).data,
		onSuccess: invalidate
	});

	return { create, update, remove };
}
